/**
 * Canonical stage vocabulary (client) — AVM-221 item 3.
 *
 * The client twin of `server/src/lib/stageVocabulary.ts`. The KEY (`StageGroup`
 * = backlog | in_progress | done) is what rides the wire and the DB; the LABEL
 * (Backlog | In Progress | Done) is render-only. Every place that used to
 * hand-write `x === 'Done' ? … : x === 'In Progress' ? …` now speaks through
 * this module so the mapping can't drift between the setup wizards, the preview
 * panel, and the Statuses board.
 *
 * The two twins are hand-kept in sync across the package boundary (no shared
 * build); a parity test asserts the label/key sets match.
 */

import type { StageGroup } from '../types';

export const STAGE_KEYS: StageGroup[] = ['backlog', 'in_progress', 'done'];

export const STAGE_LABELS: Record<StageGroup, string> = {
  backlog: 'Backlog',
  in_progress: 'In Progress',
  done: 'Done',
};

/** The ordered display labels — the Map step's <select> options. */
export const STAGE_LABEL_OPTIONS = STAGE_KEYS.map(k => STAGE_LABELS[k]);

/** Key → its human label (null passes through — an unread status stays honest). */
export function stageLabel(key: StageGroup | null | undefined): string | null {
  return key ? STAGE_LABELS[key] : null;
}

/** Label → key. The Map step speaks labels; the API stores keys. */
export function stageKeyForLabel(label: string): StageGroup {
  return label === 'Done' ? 'done' : label === 'In Progress' ? 'in_progress' : 'backlog';
}

/**
 * Jira status-category KEY → display label: done → Done, indeterminate → In
 * Progress, anything else → Backlog. A missing category yields null so the
 * caller can decide that "any explicit choice pins" (Phase B.0 pin-semantics).
 */
export function jiraCategoryKeyToLabel(category?: string | null): string | null {
  if (!category) return null;
  return category === 'done' ? 'Done' : category === 'indeterminate' ? 'In Progress' : 'Backlog';
}
